import { useEffect } from "react";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import { ProjectSummary } from "types";
import { navigateTo } from "context/navigation.service";
import Icon1 from "@assets/icons/Icon";

// Fix default marker icons broken by webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

interface Props {
  projects: ProjectSummary[];
  onClose: () => void;
}

export default function ProjectsMapModal({ projects, onClose }: Props) {
  const projectsWithCoords = projects.filter(
    (p) =>
      p.infos?.general?.coordinates?.latitude &&
      p.infos?.general?.coordinates?.longitude
  );

  // Default center: France
  const defaultCenter: [number, number] = [46.8, 2.3];
  const defaultZoom = 6;

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: "90vw", height: "80vh", maxWidth: 1200 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-row items-center justify-between px-5 py-3 border-b border-neutral-200">
          <div className="flex flex-row items-center gap-2">
            <Icon1 name="location" size={20} />
            <span className="text-h2 font-semibold text-neutral-700">
              Carte des projets
            </span>
            <span className="text-h3 text-neutral-400 ml-1">
              ({projectsWithCoords.length} localisé
              {projectsWithCoords.length > 1 ? "s" : ""})
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700 transition-colors"
          >
            <Icon1 name="close" size={20} />
          </button>
        </div>

        {/* Map */}
        <div className="flex-1">
          {projectsWithCoords.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-neutral-400">
              <Icon1 name="location" size={40} />
              <span className="text-h2">Aucun projet localisé</span>
              <span className="text-h3">
                Ajoutez une adresse avec coordonnées GPS à vos projets.
              </span>
            </div>
          ) : (
            <MapContainer
              center={defaultCenter}
              zoom={defaultZoom}
              style={{ width: "100%", height: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MarkerClusterGroup chunkedLoading>
                {projectsWithCoords.map((project) => {
                  const { latitude, longitude } =
                    project.infos.general.coordinates!;
                  return (
                    <Marker
                      key={project.id}
                      position={[latitude, longitude]}
                    >
                      <Popup>
                        <div className="flex flex-col gap-1 min-w-[160px]">
                          <span className="font-semibold text-neutral-800">
                            {project.infos.general.name}
                          </span>
                          {project.infos.general.city && (
                            <span className="text-neutral-500 text-sm">
                              {project.infos.general.city}
                            </span>
                          )}
                          <button
                            onClick={() => {
                              navigateTo({ route: `/projects/${project.id}` });
                              onClose();
                            }}
                            className="mt-1 text-sm text-blue-600 hover:underline text-left"
                          >
                            Ouvrir le projet →
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MarkerClusterGroup>
            </MapContainer>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
